/**
 * Public `/invite/accept#token=…` — the link in a team invitation an AI agent
 * asked for (migration 083, security audit 2026-09-28). No membership exists
 * until the invitee accepts here: the link works once and expires after 7
 * days.
 *
 * Built like `/signup/report`: the token lives in the URL FRAGMENT, so it
 * never reaches a server log or a Referer; the page only previews on load, and
 * accepting is a button the person presses, because mail scanners open links
 * and must not accept anything. After accepting, the person signs in with the
 * invited email as usual.
 */
import { useEffect, useState } from 'react';
import type { MemberInviteAccepted, MemberInvitePreview } from '../api/types';
import { useSession } from '../auth/session';
import { ROLE_LABELS } from '../auth/roles';
import { Link } from '../router';
import { ErrorNotice, Loading, formatDateTime } from '../ui/kit';
import { ApiError } from '../api/errors';
import { reportTokenFromHash } from './SignupReport';

const CLOSED: Record<string, string> = {
  accepted: 'This invitation was already accepted. Sign in with the invited email to use the portal.',
  revoked: 'This invitation was withdrawn by the organisation. Ask them to invite you again if you expected it.',
  expired: 'This invitation has expired. Ask the organisation to invite you again.',
};

export function InviteAccept() {
  const { api } = useSession();
  const [token] = useState(() => reportTokenFromHash(window.location.hash));
  const [preview, setPreview] = useState<MemberInvitePreview | null>(null);
  const [previewError, setPreviewError] = useState<ApiError | null>(null);
  const [loading, setLoading] = useState(Boolean(token));
  const [accepted, setAccepted] = useState<MemberInviteAccepted | null>(null);
  const [accepting, setAccepting] = useState(false);
  const [acceptError, setAcceptError] = useState<ApiError | null>(null);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    api
      .previewMemberInvite(token)
      .then((p) => !cancelled && setPreview(p))
      .catch((err: unknown) => !cancelled && setPreviewError(err instanceof ApiError ? err : null))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [api, token]);

  let body: React.ReactNode;
  if (!token) {
    body = (
      <div className="notice bad" role="alert">
        <span>This link is incomplete. Open the invitation link from the email again, or contact support@finderlyfix.com.</span>
      </div>
    );
  } else if (loading) {
    body = <Loading label="Checking the invitation" />;
  } else if (previewError || !preview) {
    body = <ErrorNotice error={previewError ?? new ApiError({ status: 404, code: 'INVITE_LINK_INVALID' })} />;
  } else if (accepted) {
    body = (
      <div className="stack-sm">
        <div className="notice ok" role="status" data-testid="invite-accepted">
          <span>
            <strong>{accepted.already_member ? 'You were already on the team.' : `You joined ${accepted.organisation}.`}</strong> Your role is{' '}
            {ROLE_LABELS[accepted.role]}. Sign in with {accepted.email} to start.
          </span>
        </div>
        <div className="row">
          <Link to="/sign-in" className="btn btn-primary">
            Sign in
          </Link>
        </div>
      </div>
    );
  } else if (preview.status !== 'open') {
    body = (
      <div className="notice info" role="status" data-testid="invite-closed">
        <span>{CLOSED[preview.status]}</span>
      </div>
    );
  } else {
    body = (
      <div className="stack-sm">
        <p>
          <strong>
            An AI agent{preview.agent_label ? ` (“${preview.agent_label}”)` : ''} invited you on behalf of {preview.organisation}
            {preview.invited_by ? `, using access that ${preview.invited_by} gave it` : ''}.
          </strong>
        </p>
        <dl className="facts" data-testid="invite-facts">
          <dt>Organisation</dt>
          <dd>{preview.organisation}</dd>
          <dt>Email</dt>
          <dd>{preview.email}</dd>
          <dt>Role</dt>
          <dd>{ROLE_LABELS[preview.role]}</dd>
          <dt>Expires</dt>
          <dd>{formatDateTime(preview.expires_at)}</dd>
        </dl>
        <p>You are not on the team until you accept. If you were not expecting this, close this page: nothing happens.</p>
        <ErrorNotice error={acceptError} />
        <div className="row">
          <button
            type="button"
            className="btn btn-primary"
            disabled={accepting}
            onClick={async () => {
              setAccepting(true);
              setAcceptError(null);
              try {
                setAccepted(await api.acceptMemberInvite(token ?? ''));
              } catch (err) {
                setAcceptError(err instanceof ApiError ? err : new ApiError({ status: 500, code: 'INTERNAL_ERROR' }));
              } finally {
                setAccepting(false);
              }
            }}
          >
            {accepting ? 'Accepting…' : `Accept and join ${preview.organisation}`}
          </button>
        </div>
      </div>
    );
  }

  return (
    <main className="shared-dashboard legal-page" id="main">
      <header className="row-between legal-head">
        <Link to="/" className="eyebrow legal-home">
          FinderlyFix Partners
        </Link>
      </header>
      <article className="stack" aria-labelledby="invite-title">
        <h1 id="invite-title">Team invitation</h1>
        {body}
      </article>
    </main>
  );
}
